import { useEffect, useMemo, useState } from "react";

import makeStyles from "@mui/styles/makeStyles";
import {
  Avatar,
  Box,
  Container,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import {
  gameSystemNameFromGameType,
  sortGroupData,
} from "./GroupPlayUtilities";
import GroupHeader from "./components/GroupHeader";
import {
  delay,
  findGetParameter,
  getData,
} from "../../../components/utilities/UtilityFunctions";
import mappingData from "./demoData/mappingData.json";
import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import { getEnvironmentVariable } from "../../../global/environmentVariables";

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 1200,
  },
  sticky: {
    paddingLeft: "4px",
    position: "sticky",
    left: 0,
    zIndex: 1,
    width: "200px",
    minWidth: "200px",
    background: theme?.palette?.background.default,
    boxShadow: "5px 2px 5px grey",

    // borderRight: "1px solid",
  },
  scoreUsed: {
    fontWeight: 600,
    textDecoration: "underline",
  },
}));

const gameTypeCells = [
  {
    id: "wcwPoints",
    numeric: true,
    align: "center",
    label: "WCP",
    key: "WCP",
    sortable: true,
  },
  {
    id: "overallPoints",
    numeric: true,
    align: "center",
    label: "Overall",
    key: "Overall",
    sortable: true,
  },
  {
    id: "generalshipPoints",
    numeric: true,
    align: "center",
    label: "Generalship",
    key: "Battle",
    sortable: true,
  },
  {
    id: "paintPoints",
    numeric: true,
    align: "center",
    label: "Paint",
    key: "Paint",
    sortable: true,
  },
  {
    id: "victoryPoints",
    numeric: true,
    align: "center",
    label: "VPs",
    key: "Victory",
    sortable: true,
  },
  {
    id: "factionPoints",
    numeric: true,
    align: "center",
    label: "Faction",
    key: "Faction",
    sortable: true,
  },
];

const headCells = [
  {
    cells: [
      {
        id: "country",
        label: "Country",
        numeric: false,
        sticky: true,
      },
      {
        id: "wcwPoints",
        numeric: true,
        align: "center",
        label: "WCP",
        sortable: true,
      },
    ],
  },
  {
    label: "Medals",
    cells: [
      {
        id: "wcwGold",
        numeric: true,
        align: "center",
        label: "Gold",
        sortable: true,
      },
      {
        id: "wcwSilver",
        numeric: true,
        align: "center",
        label: "Silver",
        sortable: true,
      },
      {
        id: "wcwBronze",
        numeric: true,
        align: "center",
        label: "Bronze",
        sortable: true,
      },
    ],
  },
  { gameType: 1, label: "Warhammer 40,000", cells: gameTypeCells },
  {
    colSpan: 5,
    gameType: 4,
    label: "Warhammer Age of Sigmar",
    cells: gameTypeCells,
  },
  {
    colSpan: 5,
    gameType: 26,
    label: "Warhammer 40,000: Kill Team",
    cells: gameTypeCells,
  },
  {
    colSpan: 5,
    gameType: 9,
    label: "Warhammer Underworlds",
    cells: gameTypeCells,
  },
];

function flatternData(row) {
  let _row = {
    ...row,
    id: row.id,
    place: row.place,
    name: row.label || row.name || "",
    countryCode: row.countryCode || "",
  };

  for (let gameType of [1, 4, 26, 9]) {
    _row[`${gameType}.WCP.Points`] =
      (row[`${gameType}.Overall.Points`] || 0) +
      (row[`${gameType}.Battle.Points`] || 0) +
      (row[`${gameType}.Paint.Points`] || 0) +
      (row[`${gameType}.Faction.Points`] || 0) +
      (row[`${gameType}.Victory.Points`] || 0);
  }

  return _row;
}

function Row({ eventGroupId, row, appRoute }) {
  const classes = useStyles();
  const gameType = findGetParameter("gameType");
  const embed = appRoute || findGetParameter("embed");
  const _darkMode = findGetParameter("darkMode") === "true";
  const _lightMode = findGetParameter("lightMode") === "true";
  const _mode = _darkMode
    ? "&darkMode=true"
    : _lightMode
      ? "&lightMode=true"
      : "";

  return (
    <TableRow
      sx={{
        borderBottom: "1px solid rgba(81, 81, 81, 1)",
      }}
    >
      <TableCell
        padding="none"
        className={classes.sticky}
        component="th"
        scope="row"
      >
        <span
          style={{
            width: "25px",
            display: "inline-block",
          }}
        >
          {row.place}
        </span>
        <Link
          href={`/world-championships-of-warhammer${
            eventGroupId === "WorldChamps24"
              ? "-24"
              : eventGroupId === "WorldChamps23"
                ? "-23"
                : ""
          }/players?${embed ? `embed=${embed}` : ""}${_mode}${
            gameType ? `&gameType=${gameType}` : ""
          }${row.countryCode ? `&countryCode=${row.countryCode}` : ""}${
            row.groupName || row.name || row.label
              ? `&groupName=${row.groupName || row.name || row.label}`
              : ""
          }`}
        >
          <Box
            sx={{
              display: "inline-block",
              mr: 1,
              paddingTop: "2px",
            }}
          >
            <Avatar
              alt="Country"
              src={`${getEnvironmentVariable(
                "SYSTEM_IMAGES_CLOUDFRONT"
              )}/system-images/countries/${row.countryCode}.svg`}
              sx={{
                width: 12,
                height: 12,
                position: "relative",
                top: "2px",
              }}
            />
          </Box>

          <span
            style={{
              display: "inline-block",
              position: "relative",
              top: "4px",
              maxWidth: "calc(100% - 45px)", // 25 + 16

              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.name}
          </span>
        </Link>
      </TableCell>

      <TableCell
        padding="none"
        align="center"
        sx={{
          minWidth: "75px",
        }}
      >
        {row.usedScores?.length > 0 ? (
          <Tooltip
            componentsProps={{
              tooltip: { sx: { maxWidth: "90vw" } },
            }}
            title={
              <Box>
                {row.usedScores.map((usedPoint, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    sx={{ display: "block" }}
                  >
                    {usedPoint.playerName}{" "}
                    {gameSystemNameFromGameType(usedPoint.gameType)}{" "}
                    {usedPoint.category} {usedPoint.points}
                  </Typography>
                ))}
              </Box>
            }
          >
            <Typography
              sx={{
                fontSize: "12px",
              }}
            >
              {row.wcwPoints || 0}
            </Typography>
          </Tooltip>
        ) : (
          row.wcwPoints || 0
        )}
      </TableCell>

      <TableCell
        padding="none"
        align="center"
        sx={{
          borderLeft: "1px solid",
        }}
      >
        {row.wcwGold || 0}
      </TableCell>
      <TableCell padding="none" align="center">
        {row.wcwSilver || 0}
      </TableCell>
      <TableCell padding="none" align="center">
        {row.wcwBronze || 0}
      </TableCell>

      {[1, 4, 26, 9].map((gameType) => (
        <>
          {[
            { key: "WCP" },
            { key: "Overall" },
            { key: "Battle", label: "generalship" },
            { key: "Paint" },
            { key: "Victory" },
            { key: "Faction" },
          ].map((pointsType, index) => (
            <TableCell
              key={`${pointsType.key}-${gameType}`}
              padding="none"
              align="center"
              className={
                pointsType.key !== "WCP" &&
                row[`${gameType}.${pointsType.key}.Used`]
                  ? classes.scoreUsed
                  : null
              }
              sx={{
                borderLeft: index === 0 ? "1px solid" : null,
              }}
            >
              <Link
                color="inherit"
                underline="none"
                href={`/world-championships-of-warhammer${
                  eventGroupId === "WorldChamps24"
                    ? "-24"
                    : eventGroupId === "WorldChamps23"
                      ? "-23"
                      : ""
                }/players?${embed ? `embed=${embed}` : ""}${
                  pointsType.key !== "WCP"
                    ? `&orderBy=wcw${pointsType.key}`
                    : ""
                }${_mode}${gameType ? `&gameType=${gameType}` : ""}${
                  row.countryCode ? `&countryCode=${row.countryCode}` : ""
                }${
                  row.groupName || row.name || row.label
                    ? `&groupName=${row.groupName || row.name || row.label}`
                    : ""
                }`}
              >
                {row[`${gameType}.${pointsType.key}.Points`] || 0}
              </Link>
            </TableCell>
          ))}
        </>
      ))}
    </TableRow>
  );
}

function Header({ orderBy, setOrderBy }) {
  const classes = useStyles();
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => {
          if (index === 0) {
            return (
              <>
                <TableCell
                  key={index + "A"}
                  align={"center"}
                  padding="none"
                  className={classes.sticky}
                />
                <TableCell key={index + "B"} align={"center"} padding="none" />
              </>
            );
          }

          return (
            <TableCell
              key={index}
              align="center"
              padding="none"
              colSpan={headCell.cells?.length || 1}
            >
              {headCell.label}
            </TableCell>
          );
        })}
      </TableRow>
      <TableRow>
        {headCells.map((headCell, index) => {
          return (
            <>
              {headCell.cells.map((childCell, childIndex) => {
                let key = headCell.gameType
                  ? `${headCell.gameType || 0}.${childCell.key || ""}.Points`
                  : `${childCell.id}`;
                return (
                  <TableCell
                    key={key}
                    align={childCell.align || "left"}
                    padding="none"
                    sortDirection={orderBy === key ? "desc" : false}
                    sx={{
                      // width: childCell.minWidth || "75px",
                      // minWidth: childCell.minWidth || "75px",

                      borderLeft:
                        index > 0 && childIndex === 0 ? "1px solid" : null,

                      "& .MuiTableSortLabel-icon":
                        orderBy === key
                          ? { color: "inherit !important" }
                          : {
                              opacity: 0.15,
                            },
                    }}
                    className={
                      index === 0 && childIndex === 0 ? classes.sticky : null
                    }
                  >
                    {childCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === key}
                        direction={"desc"}
                        onClick={() => {
                          setOrderBy(key);
                        }}
                      >
                        {childCell.label}
                        {orderBy === key ? (
                          <Box component="span" sx={visuallyHidden}>
                            sorted descending
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      childCell.label
                    )}
                  </TableCell>
                );
              })}
            </>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

export default function GroupCountryDetails({ eventGroupId, label, appRoute }) {
  const [loading, setLoading] = useState(true);
  const _orderBy = findGetParameter("orderBy");

  const updateData = async () => {
    try {
      var apiData = await getData(`/players/gwworlds`, {
        queryStringParameters: {
          groupQuery: true,
          eventGroupId: eventGroupId || "WorldChamps23",
          limit: 500,
        },
      });
      if (apiData?.data?.length) {
        for (let i = 0; i < apiData.data.length; i++) {
          apiData.data[i].name =
            apiData.data[i].label ||
            `${apiData.data[i].firstName} ${apiData.data[i].lastName}`.trim();
          apiData.data[i].countryCode =
            apiData.data[i].countryCode ||
            mappingData[apiData.data[i].label || apiData.data[i].groupName] ||
            apiData.data[i].label ||
            apiData.data[i].groupName;
        }

        apiData.data = sortGroupData(apiData.data, orderBy);
      }

      let _rows = apiData.data?.map((country) => flatternData(country));
      setRows(_rows);
    } catch (err) {
      postNotification("error", err);
    }
    setLoading(false);

    await delay(60 * 2.5 * 1000);
    updateData();
  };

  const [orderBy, setOrderBy] = useState(_orderBy || "wcwPoints");
  const [rows, setRows] = useState([]);
  const embed = appRoute || findGetParameter("embed") === "true";
  const sortedRows = useMemo(
    () => sortGroupData(rows, orderBy),
    [rows, orderBy]
  );

  useEffect(() => {
    updateData();
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          maxWidth: "100%",
          
          paddingTop:
             2 ,
        }}
      >
        <Container maxWidth="lg">
          <GroupHeader label={label} embed={embed} back />
        </Container>
      </Box>
      <Container maxWidth="lg">
        <Typography variant="h3" sx={{ mt: 1 }} gutterBottom textAlign="center">
          Country Leaders
        </Typography>

        <TableContainer style={{ maxWidth: "100%" }}>
          <Table
            sx={{
              tableLayout: "fixed",
              minWidth: 650,
              "& .MuiTableCell-root": {
                fontSize: "12px",
              },
            }}
          >
            <colgroup>
              <col style={{ width: "200px" }} />
              <col style={{ width: "75px" }} />
              <col style={{ width: "75px" }} />
              <col style={{ width: "75px" }} />
              <col style={{ width: "75px" }} />
              {[1, 4, 26, 9].map((gameType) => (
                <>
                  <col style={{ width: "75px" }} />
                  <col style={{ width: "75px" }} />
                  <col style={{ width: "75px" }} />
                  <col style={{ width: "75px" }} />
                  <col style={{ width: "75px" }} />
                  <col style={{ width: "75px" }} />
                </>
              ))}
            </colgroup>
            <Header orderBy={orderBy} setOrderBy={setOrderBy} />

            {loading ? (
              <TableBody sx={{ height: "114px" }}>
                <TableRow sx={{ borderBottom: "unset", verticalAlign: "top" }}>
                  <TableCell
                    padding="none"
                    colSpan={25}
                    sx={{ borderBottom: "unset" }}
                  >
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {sortedRows.map((row, index) => (
                  <Row
                    key={`${index}-${row.name}`}
                    row={row}
                    eventGroupId={eventGroupId}
                    appRoute={appRoute}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Container>

      {loading && <ModalLoadingFull />}
    </Box>
  );
}
